import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Client, ClientContact } from '../types';

interface UseClientsResult {
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createClient: (client: Omit<Client, 'id' | 'created_at' | 'updated_at' | 'contacts' | 'mission_count'>) => Promise<Client | null>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  // Contacts
  addContact: (contact: Omit<ClientContact, 'id' | 'created_at' | 'updated_at'>) => Promise<ClientContact | null>;
  updateContact: (id: string, updates: Partial<ClientContact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
}

export const useClients = (): UseClientsResult => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch clients + mission counts in parallel
      const [{ data: clientsData, error: clientsError }, { data: missionCounts, error: missionError }] = await Promise.all([
        supabase
          .from('clients')
          .select('*, client_contacts(*)')
          .order('name', { ascending: true }),
        supabase
          .from('missions')
          .select('client_id')
          .not('client_id', 'is', null),
      ]);

      if (clientsError) throw clientsError;
      if (missionError) throw missionError;

      const countMap = new Map<string, number>();
      (missionCounts || []).forEach((m: any) => {
        if (m.client_id) {
          countMap.set(m.client_id, (countMap.get(m.client_id) || 0) + 1);
        }
      });

      const mapped: Client[] = (clientsData || []).map((c: any) => ({
        id: c.id,
        organization_id: c.organization_id,
        name: c.name,
        short_name: c.short_name,
        sector: c.sector,
        website: c.website,
        logo_url: c.logo_url,
        notes: c.notes,
        tags: c.tags || [],
        priority: c.priority,
        siren: c.siren,
        naf_code: c.naf_code,
        categorie_entreprise: c.categorie_entreprise,
        date_creation_entreprise: c.date_creation_entreprise,
        city: c.city,
        created_by: c.created_by,
        created_at: c.created_at,
        updated_at: c.updated_at,
        contacts: (c.client_contacts || []).map((cc: any) => ({
          id: cc.id,
          client_id: cc.client_id,
          organization_id: cc.organization_id,
          full_name: cc.full_name,
          email: cc.email,
          phone: cc.phone,
          role: cc.role,
          is_primary: cc.is_primary,
          notes: cc.notes,
          created_at: cc.created_at,
          updated_at: cc.updated_at,
        })),
        mission_count: countMap.get(c.id) || 0,
      }));

      setClients(mapped);
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError(err.message || 'Erreur lors du chargement des clients');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createClient = useCallback(async (client: Omit<Client, 'id' | 'created_at' | 'updated_at' | 'contacts' | 'mission_count'>): Promise<Client | null> => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          organization_id: client.organization_id,
          name: client.name.trim(),
          short_name: client.short_name?.trim() || null,
          sector: client.sector?.trim() || null,
          website: client.website?.trim() || null,
          logo_url: client.logo_url?.trim() || null,
          notes: client.notes?.trim() || null,
          tags: client.tags || [],
          priority: client.priority || null,
          siren: client.siren || null,
          naf_code: client.naf_code || null,
          categorie_entreprise: client.categorie_entreprise || null,
          date_creation_entreprise: client.date_creation_entreprise || null,
          city: client.city || null,
          created_by: client.created_by || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Un client avec ce nom existe déjà dans votre organisation');
        }
        throw error;
      }

      await fetchClients();
      return data as Client;
    } catch (err: any) {
      console.error('Error creating client:', err);
      throw err;
    }
  }, [fetchClients]);

  const updateClient = useCallback(async (id: string, updates: Partial<Client>) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          ...(updates.name !== undefined && { name: updates.name.trim() }),
          ...(updates.short_name !== undefined && { short_name: updates.short_name?.trim() || null }),
          ...(updates.sector !== undefined && { sector: updates.sector?.trim() || null }),
          ...(updates.website !== undefined && { website: updates.website?.trim() || null }),
          ...(updates.logo_url !== undefined && { logo_url: updates.logo_url?.trim() || null }),
          ...(updates.notes !== undefined && { notes: updates.notes?.trim() || null }),
          ...(updates.tags !== undefined && { tags: updates.tags }),
          ...(updates.priority !== undefined && { priority: updates.priority }),
          ...(updates.city !== undefined && { city: updates.city?.trim() || null }),
          ...(updates.siren !== undefined && { siren: updates.siren || null }),
          ...(updates.naf_code !== undefined && { naf_code: updates.naf_code || null }),
          ...(updates.categorie_entreprise !== undefined && { categorie_entreprise: updates.categorie_entreprise || null }),
          ...(updates.date_creation_entreprise !== undefined && { date_creation_entreprise: updates.date_creation_entreprise || null }),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        if (error.code === '23505') {
          throw new Error('Un client avec ce nom existe déjà dans votre organisation');
        }
        throw error;
      }

      await fetchClients();
    } catch (err: any) {
      console.error('Error updating client:', err);
      throw err;
    }
  }, [fetchClients]);

  const deleteClient = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;
      await fetchClients();
    } catch (err: any) {
      console.error('Error deleting client:', err);
      throw err;
    }
  }, [fetchClients]);

  // ── Contact CRUD ──────────────────────────────────────────────────────────

  const addContact = useCallback(async (contact: Omit<ClientContact, 'id' | 'created_at' | 'updated_at'>): Promise<ClientContact | null> => {
    try {
      const { data, error } = await supabase
        .from('client_contacts')
        .insert({
          client_id: contact.client_id,
          organization_id: contact.organization_id,
          full_name: contact.full_name.trim(),
          email: contact.email?.trim() || null,
          phone: contact.phone?.trim() || null,
          role: contact.role?.trim() || null,
          is_primary: contact.is_primary || false,
          notes: contact.notes?.trim() || null,
        })
        .select()
        .single();

      if (error) throw error;
      await fetchClients();
      return data as ClientContact;
    } catch (err: any) {
      console.error('Error adding contact:', err);
      throw err;
    }
  }, [fetchClients]);

  const updateContact = useCallback(async (id: string, updates: Partial<ClientContact>) => {
    try {
      const { error } = await supabase
        .from('client_contacts')
        .update({
          ...(updates.full_name !== undefined && { full_name: updates.full_name.trim() }),
          ...(updates.email !== undefined && { email: updates.email?.trim() || null }),
          ...(updates.phone !== undefined && { phone: updates.phone?.trim() || null }),
          ...(updates.role !== undefined && { role: updates.role?.trim() || null }),
          ...(updates.is_primary !== undefined && { is_primary: updates.is_primary }),
          ...(updates.notes !== undefined && { notes: updates.notes?.trim() || null }),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      await fetchClients();
    } catch (err: any) {
      console.error('Error updating contact:', err);
      throw err;
    }
  }, [fetchClients]);

  const deleteContact = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('client_contacts').delete().eq('id', id);
      if (error) throw error;
      await fetchClients();
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      throw err;
    }
  }, [fetchClients]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return {
    clients,
    isLoading,
    error,
    refresh: fetchClients,
    createClient,
    updateClient,
    deleteClient,
    addContact,
    updateContact,
    deleteContact,
  };
};
